import React from 'react';
import { useParams } from 'react-router-dom';
import StreamTimerWidget from './StreamTimerWidget';
import KanbanOverlayWidget from './KanbanOverlayWidget';
import RotatingStatsPanel from './RotatingStatsPanel';
import ChatUserCircles from './ChatUserCircles';
import useTwitchChat from '../hooks/useTwitchChat';
import useTwitchStatus from '../hooks/useTwitchStatus';
import useWidgetConfig from '../hooks/useWidgetConfig';
import { overlayRootStyles } from '../overlayThemes/overlayRootStyles';

export default function StreamOverlayNoCamera() {
  const { channelName } = useParams();
  const normalizedChannel = channelName?.toLowerCase().trim() || 'itsflannelbeard';
  const { activeUsers, monitorBotUsername } = useTwitchChat(normalizedChannel);
  const { config, getEnabledWidgets } = useWidgetConfig(normalizedChannel);
  // When the stream is offline, widgets stop polling the API. The 60s
  // twitch-status poll inside this hook is what resumes them on going live.
  const { isLive } = useTwitchStatus(normalizedChannel);
  const RIGHT_OVERLAY_RESERVED_WIDTH = 667;

  const WIDGET_CSS_CLASS = { timer: 'widget-timer', kanban: 'widget-kanban', rotatingStats: 'widget-rotating' };

  const renderWidget = (key) => {
    switch (key) {
      case 'timer': return <StreamTimerWidget compact={true} flannelStyle={true} active={isLive} />;
      case 'kanban': return <KanbanOverlayWidget compact={true} flannelStyle={true} active={isLive} />;
      case 'rotatingStats': return <RotatingStatsPanel channelName={normalizedChannel} config={config} active={isLive} />;
      default: return null;
    }
  };

  const enabledWidgets = getEnabledWidgets();

  return (
    <div className="flannel-overlay-container">
      {/* Top area is transparent for game capture */}
      
      {/* Bottom Flannel Panel with embedded widgets */}
      <div className="flannel-bottom-panel">
        {/* Pixelated Flannel Background */}
        <div className="flannel-pattern"></div>
        
        {/* Decorative pixel border at top of panel */}
        <div className="pixel-border-top"></div>
        
        {/* Widget Row */}
        <div className="widget-row">
          {enabledWidgets.map(w => (
            <div key={w.key} className={`flannel-widget ${WIDGET_CSS_CLASS[w.key] || ''}`}>
              {renderWidget(w.key)}
            </div>
          ))}
        </div>
      </div>

      {/* Chat Window - Bottom Right */}
      <div className="overlay-chat">
        <div className="chat-pixel-border">
          <div className="chat-window">
          </div>
        </div>
      </div>

      {/* Chat User Stars - Bouncing 8-bit stars at bottom */}
      <ChatUserCircles
        users={activeUsers}
        channelName={normalizedChannel}
        monitorBotUsername={monitorBotUsername}
        reservedRightWidth={RIGHT_OVERLAY_RESERVED_WIDTH}
      />

      <style>{`
        ${overlayRootStyles}

        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');

        .flannel-overlay-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          background: transparent;
          font-family: 'Press Start 2P', 'JetBrains Mono', monospace;
        }

        /* Bottom Flannel Panel */
        .flannel-bottom-panel {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 160px;
          z-index: 10;
        }

        /* Tartan Plaid Pattern - Rusty Red, Teal, Cream */
        .flannel-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          --rust: #a65241;
          --teal: #1a6e7a;
          --cream: #d4c4a8;
          --dark-teal: #0d4a54;
          --blend-rust-teal: #5a5a5a;
          background-color: var(--rust);
        }
        
        /* Horizontal stripes layer */
        .flannel-pattern::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            repeating-linear-gradient(
              0deg,
              var(--rust) 0px,
              var(--rust) 24px,
              var(--cream) 24px,
              var(--cream) 40px,
              var(--rust) 40px,
              var(--rust) 48px,
              var(--teal) 48px,
              var(--teal) 96px,
              var(--rust) 96px,
              var(--rust) 104px,
              var(--cream) 104px,
              var(--cream) 120px,
              var(--rust) 120px,
              var(--rust) 144px
            );
          background-size: 100% 144px;
        }
        
        /* Vertical stripes with blend mode */
        .flannel-pattern::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 24px,
              rgba(212, 196, 168, 0.7) 24px,
              rgba(212, 196, 168, 0.7) 40px,
              transparent 40px,
              transparent 48px,
              rgba(26, 110, 122, 0.8) 48px,
              rgba(26, 110, 122, 0.8) 96px,
              transparent 96px,
              transparent 104px,
              rgba(212, 196, 168, 0.7) 104px,
              rgba(212, 196, 168, 0.7) 120px,
              transparent 120px,
              transparent 144px
            );
          background-size: 144px 100%;
          mix-blend-mode: multiply;
        }

        /* Pixel art style border at top - stitching effect */
        .pixel-border-top {
          position: absolute;
          top: -4px;
          left: 0;
          right: 0;
          height: 4px;
          background: 
            repeating-linear-gradient(
              90deg,
              #d4c4a8 0px,
              #d4c4a8 8px,
              transparent 8px,
              transparent 16px
            );
          image-rendering: pixelated;
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5));
          z-index: 5;
        }

        /* Widget Row */
        .widget-row {
          position: relative;
          z-index: 2;
          display: flex;
          flex-wrap: nowrap;
          gap: 13px;
          padding: 16px 27px;
          padding-right: 667px; /* Space for chat window (640px + margin) */
          align-items: stretch;
          justify-content: space-between;
          height: 100%;
        }

        /* Flannel-embedded widgets */
        .flannel-widget {
          background: rgba(13, 74, 84, 0.95);
          border: 4px solid #d4c4a8;
          border-radius: 0;
          padding: 13px 21px;
          box-shadow: 
            4px 4px 0 #0d4a54,
            inset 0 0 0 1px rgba(212, 196, 168, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 93px;
          overflow: hidden;
        }

        .widget-timer {
          flex: 0 0 15%;
        }

        .widget-kanban {
          flex: 0 0 35%;
        }

        .widget-rotating {
          flex: 0 0 calc(50% - 26px);
        }

        /* Chat Window */
        .overlay-chat {
          position: absolute;
          bottom: 16px;
          right: 21px;
          z-index: 50;
          width: 640px;
          height: 225px;
        }

        .chat-pixel-border {
          width: 100%;
          height: 100%;
          padding: 5px;
          background: 
            linear-gradient(135deg, #a65241 0%, #d4c4a8 25%, #1a6e7a 50%, #d4c4a8 75%, #a65241 100%);
          box-shadow: 
            5px 5px 0 #0d4a54,
            0 0 21px rgba(166, 82, 65, 0.4);
        }

        .chat-window {
          width: 100%;
          height: 100%;
          background: #0a0a0a;
          border: 3px solid #0d4a54;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-placeholder {
          color: #d4c4a8;
          font-size: 1rem;
          text-align: center;
          padding: 27px;
          opacity: 0.6;
        }

        /* Override compact widget styles for tartan theme */
        .flannel-widget .timer-flannel-label,
        .flannel-widget .kanban-flannel-label {
          color: #d4c4a8 !important;
        }

        .flannel-widget .timer-flannel-value {
          color: #f5f0e6 !important;
          text-shadow: 1px 1px 0 #a65241;
        }

        .flannel-widget .kanban-flannel-value {
          color: #f5f0e6 !important;
          text-shadow: 1px 1px 0 #a65241;
        }
      `}</style>
    </div>
  );
}

